import { createContext, useState, type ReactNode } from "react";
import { v4 as uuid } from "uuid";

import { getConfig } from "lib/get-config";
import { Toast, type ToastVariant } from "lib/ui/toast";

import { BookmarkNotification } from "app/bookmark-notification";

type BookmarkNotificationType = "added" | "deleted";

type ToastNotification = {
  id: string;
  title: string;
  message?: string;
  variant?: ToastVariant;
  autoClose?: number;
};

type ToastOptions = {
  message?: string;
  variant?: ToastVariant;
  autoClose?: number;
};

type NotificationsContextValue = {
  showBookmarkNotification: (type: BookmarkNotificationType) => void;
  showToast: (title: string, options?: ToastOptions) => void;
};

export const NotificationsContext =
  createContext<NotificationsContextValue | null>(null);
NotificationsContext.displayName = "NotificationsContext";

type NotificationsProviderProps = {
  children: ReactNode;
};

export function NotificationsProvider({
  children,
}: NotificationsProviderProps) {
  const [bookmarkNotification, setBookmarkNotification] =
    useState<BookmarkNotificationType | null>(null);
  const [toasts, setToasts] = useState<ToastNotification[]>(() => {
    const { notification, notificationType } = getConfig();
    if (!notification) return [];
    return [
      {
        id: uuid(),
        title: notification,
        variant: notificationType,
        autoClose: 0,
      },
    ];
  });

  function showBookmarkNotification(type: BookmarkNotificationType) {
    setBookmarkNotification(type);
  }

  function hideBookmarkNotification() {
    setBookmarkNotification(null);
  }

  function showToast(title: string, options?: ToastOptions) {
    const id = uuid();
    setToasts(prev => [...prev, { id, title, ...options }]);
  }

  function hideToast(id: string) {
    setToasts(prev => prev.filter(t => t.id !== id));
  }

  return (
    <NotificationsContext value={{ showBookmarkNotification, showToast }}>
      {children}
      {bookmarkNotification && (
        <BookmarkNotification
          type={bookmarkNotification}
          onClose={hideBookmarkNotification}
        />
      )}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          title={toast.title}
          message={toast.message}
          variant={toast.variant}
          autoClose={toast.autoClose}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </NotificationsContext>
  );
}
